// revisions.ts — Endpoints révisions + matrices/évolutions/historique.
import {
  downloadBlob,
  downloadGetBlob,
  req,
  upload,
  uploadBlobWithFilename,
} from './_internals.js';

export const revisionsApi = {
  getRevisions: (pid: number | string) => req('GET', '/revisions/project/' + pid),
  startRevision: (pid: number | string, d: unknown) =>
    req('POST', '/revisions/project/' + pid + '/start', d),
  getRevision: (id: number | string) => req('GET', '/revisions/' + id),
  updateRevision: (id: number | string, d: unknown) => req('PUT', '/revisions/' + id, d),
  closeRevision: (id: number | string) => req('PUT', '/revisions/' + id + '/close'),
  saveRevision: (id: number | string, d: unknown) => req('POST', '/revisions/' + id + '/save', d),
  exportRevisionPptx: (revId: number | string, body: unknown): Promise<Blob> =>
    downloadBlob(`/revisions/${revId}/export-pptx`, body),
  exportRevisionXlsxUrl: (
    id: number | string,
    includeActions: boolean,
    userId: number | string | null | undefined,
    exportDt: string,
  ): string => {
    const p = new URLSearchParams({
      include_actions: includeActions ? 'true' : 'false',
      export_dt: exportDt,
    });
    if (userId) p.set('user_id', String(userId));
    return `/api/v1/revisions/${id}/export-xlsx?${p}`;
  },
  updateActionInRevision: (id: number | string, d: unknown) =>
    req('PUT', '/revisions/actions/' + id, d),
  getRiskMatrix: (pid: number | string) => req('GET', '/revisions/project/' + pid + '/risk-matrix'),
  getRiskEvolution: (pid: number | string, params?: Record<string, string> | null) =>
    req(
      'GET',
      '/revisions/project/' +
        pid +
        '/risk-evolution' +
        (params ? '?' + new URLSearchParams(params).toString() : ''),
    ),
  getRiskHistory: (pid: number | string, rid: number | string) =>
    req('GET', '/revisions/project/' + pid + '/risk-history/' + rid),

  // ── Challenger IA ──────────────────────────────────────────────────────
  // Le dossier pseudonymisé — ce qui SORTIRAIT. Aucun appel à un fournisseur
  // n'est fait ici : pas de crédit, pas de consentement au transfert hors UE à
  // vérifier. Un tenant qui a refusé le transfert garde la fonctionnalité par
  // le kit.
  getChallengeDossier: (id: number | string) =>
    req('GET', '/revisions/' + id + '/challenge/dossier'),
  // L'aperçu AVEC le document de contexte joint. `POST` parce qu'il porte un
  // fichier, pas parce qu'il écrit : rien n'est enregistré, aucun fournisseur
  // n'est appelé, aucun crédit n'est consommé. C'est ce qui permet à
  // l'utilisateur de CONSTATER que son compte rendu est pseudonymisé lui aussi.
  previewChallengeDossier: (id: number | string, form: FormData) =>
    upload('/revisions/' + id + '/challenge/preview', form),
  downloadChallengeKit: (id: number | string, pseudonymized: boolean, filename: string) =>
    downloadGetBlob(
      `/revisions/${id}/challenge/dossier?format=kit&pseudonymized=${pseudonymized}`,
      filename,
    ),
  // L'analyse. SYNCHRONE : la réponse EST le document. Les 5 crédits sont
  // vérifiés avant le départ et décomptés seulement à la réussite — un échec ne
  // coûte rien.
  challengeRevision: (id: number | string, form: FormData, fallbackName: string) =>
    uploadBlobWithFilename('/revisions/' + id + '/challenge', form, fallbackName),

  // Vues agrégées multi-projets — pas dans /revisions/* mais sémantiquement
  // alignées (snapshots croisés).
  // `scope='program'` : le backend ne retient que les risques escaladés au
  // programme (escalated_to_program=True). Omis = contexte projet (tous les
  // risques). Cf. flag « Escalade au programme ».
  getAggregatedMatrix: (projectIds: ReadonlyArray<number | string>, scope?: 'program') =>
    req(
      'GET',
      '/risk-snapshots/matrix?project_ids=' +
        projectIds.join(',') +
        (scope ? '&scope=' + scope : ''),
    ),
  getAggregatedEvolution: (projectIds: ReadonlyArray<number | string>, scope?: 'program') =>
    req(
      'GET',
      '/risk-snapshots/evolution?project_ids=' +
        projectIds.join(',') +
        (scope ? '&scope=' + scope : ''),
    ),
};
